import React, { useCallback, useState } from 'react';
import { FormHelperText, Grid, Stack, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import getFileData, { FileResultType } from '../../Helper/file';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export interface DragDropProps {
  accept:
    | string
    | {
        [key: string]: [];
      };
  subText: string;
  csnSubText?: string;
  onChange: (fileData: FileResultType[], event?: DropEvent | React.ChangeEvent<HTMLInputElement>) => void;
  readType?: 'xls' | 'base64';
  wrapperOnClickRegister?: boolean;
}

const Wrapper = styled(Grid)(({ theme }) => ({
  padding: '16px 24px',
  border: `1px solid #EAECF0`,
  borderRadius: '8px',
  cursor: 'pointer',
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
}));

const Label = styled('label')(() => ({
  width: '100%',
  height: 'inherit',
}));

function DragDrop(props: DragDropProps) {
  const [error, setError] = useState<FileRejection[]>();
  const [fileUploaded, setFileUploaded] = useState(false);

  const onDrop = useCallback(
    async (
      acceptedFiles: File[],
      fileRejections: FileRejection[],
      event: DropEvent | React.ChangeEvent<HTMLInputElement>,
    ) => {
      if ('preventDefault' in event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }

      setError(fileRejections);
      const fileResult = (await getFileData(acceptedFiles, props.readType)) as FileResultType[];
      if (fileResult.length) {
        setFileUploaded(true);
      }
      props.onChange(fileResult, event);
    },
    [props],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: typeof props.accept === 'object' ? props.accept : { [props.accept]: [], 'application/pdf': [] },
  });

  const rootProps = getRootProps();

  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.currentTarget.value = '';
  };

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  return (
    <Label>
      <Wrapper
        container
        alignContent="center"
        height="inherit"
        alignItems="center"
        onDrop={rootProps.onDrop}
        style={{ overflow: 'hidden' }}
        onClick={props.wrapperOnClickRegister ? rootProps.onClick : undefined}
      >
        <Grid container item justifyContent="center">
          {fileUploaded ? (
            <CheckCircleIcon sx={{ fontSize: 60, color: 'green' }} />
          ) : (
            <CloudUploadIcon sx={{ fontSize: 60, color: '#5591F0' }} />
          )}
        </Grid>

        <Grid container item justifyContent="center" className="font-semibold mt-1">
          <Typography variant="inherit" color="#5591F0" mr="5px">
            {fileUploaded ? 'File Uploaded' : 'Click to Upload'}
          </Typography>
          {!fileUploaded && <>or Drag & drop file</>}
        </Grid>

        <Stack textAlign="center" sx={{ width: '100%' }}>
          <Typography variant="subtitle2" className="sb-text-gray">
            {props.subText}
          </Typography>
          {name === 'Cargo Solutions Network' && (
            <Typography variant="subtitle2" className="sb-text-gray">
              {props.csnSubText}
            </Typography>
          )}
        </Stack>

        <input
          {...getInputProps({ onClick: handleInputClick })}
          accept={typeof props.accept === 'object' ? Object.keys(props.accept).join(',') : props.accept}
          style={{ display: 'none' }}
        />
      </Wrapper>

      {error?.length
        ? error[0].errors.map((err, index) => (
            <FormHelperText error key={index}>
              {err.message}
            </FormHelperText>
          ))
        : null}
    </Label>
  );
}

export default DragDrop;
